<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";
import { useMessageStore } from "@/stores/message";
import { getAllTabMenus } from "@/config/view-config";
import { IsMenuGroup, type Menu, type MenuGroup } from "@/types/webStyle";
import { PostLogout } from "@/apis/user";
import { useCurrentInstance } from "@/util";

// props：通过 v-model:visible 控制抽屉显隐
defineProps<{
  visible: boolean;
}>();
const emit = defineEmits<{
  (e: "update:visible", val: boolean): void;
}>();

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const messageStore = useMessageStore();
const { globalProperties } = useCurrentInstance();

// 构建完整的菜单树：每个 tab 对应一组子菜单
const menuTree = computed(() => getAllTabMenus(userStore));

const isLogin = computed(() => userStore.getUserId > 0);
const username = computed(() => userStore.getUsername);
const unreadCount = computed(() => messageStore.unreadCount);

// 当前激活的顶部菜单名（用于高亮）
const activeTab = computed(() => route.meta.tab as string | undefined);

// 当前激活的菜单项 value（用于 t-menu 高亮）
// 优先用 sidebar（具体子菜单项 name），为空时 fallback 到 tab 名（首页等无子菜单场景）
const activeMenuValue = computed(() => {
  const sidebar = route.meta.sidebar as string | undefined;
  if (sidebar) return sidebar;
  return activeTab.value;
});

// 展开的 submenu 集合（受控）
// 默认展开当前 tab，用户可手动展开/收起其他 tab
const expandedMenus = ref<string[]>([]);
watch(
  () => route.meta.tab,
  (tab) => {
    if (tab) {
      // 当前 tab 加入展开列表（不替换，保留用户手动展开的其他 tab）
      if (!expandedMenus.value.includes(tab as string)) {
        expandedMenus.value = [...expandedMenus.value, tab as string];
      }
    }
  },
  { immediate: true }
);

const onExpand = (val: string[]) => {
  expandedMenus.value = val;
};

// 关闭抽屉的统一入口
const close = () => emit("update:visible", false);

// 路由跳转后自动关闭抽屉
router.afterEach(() => {
  close();
});

const handleUserAction = async (action: "message" | "profile" | "settings" | "logout") => {
  close();
  switch (action) {
    case "message":
      router.push({ name: "message" });
      break;
    case "profile":
      router.push({ path: "/user/" + username.value });
      break;
    case "settings":
      router.push({ name: "user-modify" });
      break;
    case "logout":
      // 注销逻辑（与 HeaderContent 保持一致：调 API + 清 store + 跳转）
      try {
        await PostLogout();
      } catch (e) {
        console.error("Logout failed:", e);
      } finally {
        userStore.clear();
        messageStore.clearUnread();
        globalProperties.$message.success({ duration: 3000, content: "注销成功" });
        router.push({
          path: "/login",
          query: { redirect_uri: route.fullPath },
        });
      }
      break;
  }
};

const handleLoginClick = () => {
  close();
  if (route.name !== "login") {
    router.push({
      name: "login",
      query: { redirect_uri: route.fullPath },
    });
  }
};
</script>

<template>
  <t-drawer
    :visible="visible"
    placement="left"
    size="80%"
    :close-on-esc-keydown="true"
    :show-overlay="true"
    header
    :footer="false"
    class="mobile-nav-drawer"
    @update:visible="(v: boolean) => emit('update:visible', v)"
  >
    <template #header>
      <router-link to="/" class="mobile-logo" @click="close">DidaOJ</router-link>
    </template>

    <div class="mobile-nav-body">
      <!-- 统一的导航菜单：有子菜单的 tab 用 t-submenu（可折叠），无子菜单的用 t-menu-item -->
      <t-menu
        :value="activeMenuValue"
        theme="light"
        expand-type="normal"
        :expanded="expandedMenus"
        @expand="onExpand"
        @change="close"
      >
        <template v-for="item in menuTree" :key="item.tab.name">
          <!-- 有子菜单的 tab：用 t-submenu 可折叠 -->
          <t-submenu
            v-if="item.subMenus.length > 0"
            :value="item.tab.name"
          >
            <template #icon>
              <t-icon v-if="item.tab.icon" :name="item.tab.icon" />
            </template>
            <template #title>{{ item.tab.title }}</template>

            <!-- 渲染子菜单（可能是 Menu 或 MenuGroup） -->
            <template v-for="(sub, idx) in item.subMenus" :key="idx">
              <t-menu-group
                v-if="IsMenuGroup(sub)"
                :title="sub.title"
              >
                <t-menu-item
                  v-for="child in (sub as MenuGroup).children"
                  :key="child.name"
                  :value="child.name"
                  :to="child.to"
                >
                  <template v-if="child.icon" #icon>
                    <t-icon :name="child.icon" :class="child.iconClass" />
                  </template>
                  {{ child.title }}
                </t-menu-item>
              </t-menu-group>
              <t-menu-item
                v-else
                :value="(sub as Menu).name"
                :to="(sub as Menu).to"
              >
                <template v-if="(sub as Menu).icon" #icon>
                  <t-icon :name="(sub as Menu).icon" :class="(sub as Menu).iconClass" />
                </template>
                {{ (sub as Menu).title }}
              </t-menu-item>
            </template>
          </t-submenu>

          <!-- 无子菜单的 tab（如首页）：直接 t-menu-item 跳转 -->
          <t-menu-item
            v-else
            :value="item.tab.name"
            :to="{ name: item.tab.redirect || item.tab.name }"
          >
            <template v-if="item.tab.icon" #icon>
              <t-icon :name="item.tab.icon" />
            </template>
            {{ item.tab.title }}
          </t-menu-item>
        </template>
      </t-menu>

      <!-- 用户区 -->
      <div class="mobile-user-section">
        <div class="mobile-section-title">账户</div>
        <div class="mobile-user-list">
          <template v-if="isLogin">
            <div class="mobile-user-item" @click="handleUserAction('message')">
              <span class="mobile-user-item-title">私信</span>
              <t-badge
                v-if="unreadCount > 0"
                :count="unreadCount"
                :max-count="99"
                shape="round"
              />
            </div>
            <div class="mobile-user-item" @click="handleUserAction('profile')">
              <span class="mobile-user-item-title">个人空间</span>
              <t-icon name="chevron-right" class="mobile-user-item-arrow" />
            </div>
            <div class="mobile-user-item" @click="handleUserAction('settings')">
              <span class="mobile-user-item-title">用户设置</span>
              <t-icon name="chevron-right" class="mobile-user-item-arrow" />
            </div>
            <div class="mobile-user-item mobile-user-logout" @click="handleUserAction('logout')">
              <span class="mobile-user-item-title">注销</span>
              <t-icon name="poweroff" class="mobile-user-item-arrow" />
            </div>
          </template>
          <div v-else class="mobile-user-item" @click="handleLoginClick">
            <span class="mobile-user-item-title">登录 / 注册</span>
            <t-icon name="chevron-right" class="mobile-user-item-arrow" />
          </div>
        </div>
      </div>
    </div>
  </t-drawer>
</template>

<style scoped>
.mobile-logo {
  color: inherit;
  text-decoration: none;
  font-size: 18px;
  font-weight: bold;
  font-family: "Source Code Pro", monospace;
}

.mobile-nav-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 8px;
}

.mobile-section-title {
  font-size: 12px;
  color: var(--td-text-color-placeholder, #999);
  padding: 8px 16px 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.mobile-user-section {
  border-top: 1px solid var(--td-component-stroke, #e7e7e7);
  padding-top: 8px;
}

/* 用户区列表项 */
.mobile-user-list {
  display: flex;
  flex-direction: column;
}

.mobile-user-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  user-select: none;
}

.mobile-user-item:hover,
.mobile-user-item:active {
  background-color: var(--td-bg-color-container-hover, #f5f5f5);
}

.mobile-user-item-title {
  font-size: 14px;
  color: var(--td-text-color-primary, #333);
}

.mobile-user-item-arrow {
  color: var(--td-text-color-placeholder, #999);
  font-size: 16px;
}

.mobile-user-logout .mobile-user-item-title {
  color: var(--td-error-color, #e34d59);
}

.mobile-user-logout .mobile-user-item-arrow {
  color: var(--td-error-color, #e34d59);
}

/* 让 t-menu 在抽屉中占满宽度 */
.mobile-nav-body :deep(.t-menu) {
  width: 100%;
}

.mobile-nav-body :deep(.t-menu__inner) {
  width: 100%;
}

.mobile-nav-body :deep(.t-menu-item) {
  width: 100%;
}

/* submenu 平铺展开样式调整 */
.mobile-nav-body :deep(.t-submenu) {
  width: 100%;
}

.mobile-nav-body :deep(.t-submenu .t-menu-item) {
  padding-left: 40px;
}
</style>
